// Standard Sizes Definitions (Dictionary for Name Lookup)
const EXCLUSION_RULES = [
    // { trigger: { input: 'size', values: ['A1'] }, action: { target: 'paper', mode: 'disable', values: ['handle'] } }
];

// Used to map "Name Only" variants to dimensions.
const STANDARD_SIZE_DICT = {
    "A5": [148, 210], "A4": [210, 297], "A3": [297, 420], "A3ノビ": [329, 483],
    "A2": [420, 594], "A2ノビ": [432, 610], "A1": [594, 841], "A1ノビ": [610, 914],
    "A0": [841, 1189], "A0ノビ": [914, 1292],
    "B5": [182, 257], "B4": [257, 364], "B3": [364, 515], "B2": [515, 728],
    "B1": [728, 1030], "B1ノビ": [790, 1118], "B0": [1030, 1456], "B0ノビ": [1118, 1580],
    "L": [89, 127], "2L": [128, 178], "ハガキ": [100, 148],
    "六切": [203, 254], "四切": [254, 305], "ワイド四切": [254, 365],
    "半切": [356, 432], "全紙": [457, 559], "大全紙": [508, 610], "全倍": [600, 900],
    "SM": [158, 227],
    "F4": [242, 333], "F6": [318, 410], "F8": [380, 455], "F10": [455, 530], "F15": [530, 652], "F20": [606, 727]
};

// Lightweight Image Header Parser for Resolution Extraction
class ImageMetadataReader {
    static async read(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            // Read first 64KB - sufficient for most headers (TIFF/PSD might need more but IFD is usually early)
            const BLOB_SIZE = 64 * 1024;
            const blob = file.slice(0, BLOB_SIZE);

            reader.onload = (e) => {
                try {
                    const view = new DataView(e.target.result);
                    const result = this.parse(view, file.type, file.size);

                    // [NEW] Handle TIFF Delayed IFD
                    if (result && result.offsetNeeded) {
                        const needed = result.offsetNeeded;
                        // Read 4KB for IFD (usually enough for standard tags)
                        const blob2 = file.slice(needed, needed + 4096);
                        const reader2 = new FileReader();
                        reader2.onload = (e2) => {
                            try {
                                const view2 = new DataView(e2.target.result);
                                // Parse IFD assuming it starts at index 0 of this new view
                                const res2 = this.parseTIFFInView(view2, 0, result.le);
                                resolve(res2);
                            } catch (err2) {
                                console.warn("TIFF IFD Parse failed", err2);
                                resolve(null);
                            }
                        };
                        reader2.onerror = () => resolve(null);
                        reader2.readAsArrayBuffer(blob2);
                        return;
                    }

                    resolve(result);
                } catch (err) {
                    console.warn("Metadata parse failed:", err);
                    resolve(null); // Fail gracefully
                }
            };
            reader.onerror = () => resolve(null);
            reader.readAsArrayBuffer(blob);
        });
    }

    static parse(view, type, fileSize) {
        // 1. JPEG (Start of Image marker FF D8)
        if (view.getUint16(0) === 0xFFD8) {
            return this.parseJPEG(view);
        }
        // 2. PNG (89 50 4E 47 ...)
        if (view.getUint32(0) === 0x89504E47) {
            return {
                width: view.getUint32(16),
                height: view.getUint32(20),
                format: 'PNG'
            };
        }
        // 3. GIF (GIF8...)
        if (view.getUint32(0) === 0x47494638) {
            return {
                width: view.getUint16(6, true),
                height: view.getUint16(8, true),
                format: 'GIF'
            };
        }
        // 4. BMP (BM...)
        if (view.getUint16(0) === 0x424D) {
            return {
                width: view.getInt32(18, true),
                height: Math.abs(view.getInt32(22, true)),
                format: 'BMP'
            };
        }
        // 5. WebP (RIFF....WEBP)
        if (view.getUint32(0) === 0x52494646 && view.getUint32(8) === 0x57454250) {
            return this.parseWebP(view);
        }
        // 6. PSD (8BPS)
        if (view.getUint32(0) === 0x38425053) {
            return {
                width: view.getUint32(18),
                height: view.getUint32(14),
                format: 'PSD'
            };
        }
        // 7. TIFF (II or MM)
        // 2 bytes for byte order
        if (view.byteLength >= 2) {
            const tiffMarker = view.getUint16(0);
            if (tiffMarker === 0x4949 || tiffMarker === 0x4D4D) {
                return this.parseTIFF(view, tiffMarker === 0x4949);
            }
        }

        return null;
    }

    static parseJPEG(view) {
        let offset = 2;
        const length = view.byteLength;
        while (offset < length) {
            const marker = view.getUint16(offset);
            offset += 2;
            if (marker === 0xFFC0 || marker === 0xFFC2) { // SOF0 or SOF2
                return {
                    height: view.getUint16(offset + 3),
                    width: view.getUint16(offset + 5),
                    format: 'JPEG'
                };
            }
            offset += view.getUint16(offset);
        }
        return null;
    }

    static parseWebP(view) {
        // VP8 chunk check
        // Simple parsing for VP8/VP8L/VP8X is complex. 
        // RIFF header (12) + Chunk Header (8).
        const chunkHeader = String.fromCharCode(
            view.getUint8(12), view.getUint8(13), view.getUint8(14), view.getUint8(15)
        );
        if (chunkHeader === 'VP8 ') {
            // Keyframe check needed but basic lossy webp:
            // 7 bytes offset from chunk + 3 bytes frame tag. 
            // Width is 2 bytes at offset 26 (Little Endian)
            return {
                width: view.getUint16(26, true) & 0x3fff,
                height: view.getUint16(28, true) & 0x3fff,
                format: 'WebP'
            };
        }
        if (chunkHeader === 'VP8X') {
            // Extended
            // Width at 24 (3 bytes), Height at 27 (3 bytes)
            const w1 = view.getUint8(24);
            const w2 = view.getUint8(25);
            const w3 = view.getUint8(26);
            const width = (w3 << 16) | (w2 << 8) | w1;

            const h1 = view.getUint8(27);
            const h2 = view.getUint8(28);
            const h3 = view.getUint8(29);
            const height = (h3 << 16) | (h2 << 8) | h1;
            return { width: width + 1, height: height + 1, format: 'WebP' };
        }
        return null; // VP8L logic skipped for brevity
    }

    static parseTIFF(view, littleEndian) {
        if (view.byteLength < 8) return null;
        const ifdOffset = view.getUint32(4, littleEndian);

        // Delegate
        return this.parseTIFFInView(view, ifdOffset, littleEndian);
    }

    static parseTIFFInView(view, offset, littleEndian) {
        // Check bounds
        if (offset + 2 > view.byteLength) {
            return { offsetNeeded: offset, le: littleEndian };
        }

        const entries = view.getUint16(offset, littleEndian);

        let width, height;
        for (let i = 0; i < entries; i++) {
            const entryOffset = offset + 2 + (i * 12);
            if (entryOffset + 12 > view.byteLength) {
                // Incomplete IFD data, return what we have or offset needed? 
                // Hard to recover partial IFD, assume failing
                break;
            }

            const tag = view.getUint16(entryOffset, littleEndian);
            if (tag === 256) { // ImageWidth
                // Type 3 (SHORT) or 4 (LONG)
                const type = view.getUint16(entryOffset + 2, littleEndian);
                // Value is inside 4 bytes unless count > 1 (rare for W/H)
                width = (type === 3) ? view.getUint16(entryOffset + 8, littleEndian) : view.getUint32(entryOffset + 8, littleEndian);
            } else if (tag === 257) { // ImageLength (Height)
                const type = view.getUint16(entryOffset + 2, littleEndian);
                height = (type === 3) ? view.getUint16(entryOffset + 8, littleEndian) : view.getUint32(entryOffset + 8, littleEndian);
            }

            if (width && height) {
                return { width, height, format: 'TIFF' };
            }
        }
        return null;
    }
}


class RFModal {
    static createBase() {
        let overlay = document.querySelector('.rf-modal-overlay');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.className = 'rf-modal-overlay';
            overlay.innerHTML = `
                <div class="rf-modal-content">
                    <div class="rf-modal-title"></div>
                    <div class="rf-modal-message"></div>
                    <div class="rf-modal-actions"></div>
                </div>
            `;
            document.body.appendChild(overlay);
        }
        return overlay;
    }

    static alert(title, message) {
        const overlay = this.createBase();
        overlay.querySelector('.rf-modal-title').textContent = title;
        overlay.querySelector('.rf-modal-message').innerHTML = message;

        const actions = overlay.querySelector('.rf-modal-actions');
        actions.innerHTML = `<button class="rf-btn-modal rf-btn-primary">OK</button>`;

        requestAnimationFrame(() => overlay.classList.add('rf-active'));

        return new Promise(resolve => {
            const btn = actions.querySelector('button');
            const close = () => {
                overlay.classList.remove('rf-active');
                setTimeout(() => resolve(), 300);
            };
            btn.onclick = close;
            overlay.onclick = (e) => { if (e.target === overlay) close(); };
        });
    }

    static confirm(title, message) {
        const overlay = this.createBase();
        overlay.querySelector('.rf-modal-title').textContent = title;
        overlay.querySelector('.rf-modal-message').innerHTML = message.replace(/\n/g, '<br>');

        const actions = overlay.querySelector('.rf-modal-actions');
        actions.innerHTML = `
            <button class="rf-btn-modal rf-btn-secondary" id="rf-modal-cancel">キャンセル</button>
            <button class="rf-btn-modal rf-btn-primary" id="rf-modal-confirm">変更する</button>
        `;

        requestAnimationFrame(() => overlay.classList.add('rf-active'));

        return new Promise(resolve => {
            const close = (result) => {
                overlay.classList.remove('rf-active');
                setTimeout(() => resolve(result), 300);
            };
            document.getElementById('rf-modal-confirm').onclick = () => close(true);
            document.getElementById('rf-modal-cancel').onclick = () => close(false);
            overlay.onclick = (e) => { if (e.target === overlay) close(false); };
        });
    }
}

function preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
}

/**
 * Advanced Thumbnail Generator
 * Dynamically loads parser libraries to render thumbnails for complex formats.
 */
class ThumbnailGenerator {
    constructor() {
        this.scripts = {
            pdf: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js',
            pdfWorker: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js',
            psd: 'https://cdnjs.cloudflare.com/ajax/libs/psd.js/3.2.0/psd.min.js',
            tiff: 'https://cdn.jsdelivr.net/npm/utif@2.0.1/UTIF.js'
        };
        this.loaded = {};
    }

    async loadScript(src, checkVar) {
        if (window[checkVar]) return Promise.resolve();
        if (this.loaded[src]) return this.loaded[src];

        this.loaded[src] = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error(`Failed to load ${src}`));
            document.head.appendChild(script);
        });
        return this.loaded[src];
    }

    async generate(file) {
        const ext = file.name.split('.').pop().toLowerCase();

        try {
            if (['pdf', 'ai'].includes(ext)) {
                return await this.renderPDF(file);
            } else if (['psd'].includes(ext)) {
                return await this.renderPSD(file);
            } else if (['tiff', 'tif'].includes(ext)) {
                return await this.renderTIFF(file);
            }
        } catch (e) {
            console.warn(`Thumbnail generation failed for ${file.name}:`, e);
            return null;
        }
        return null;
    }

    async renderPDF(file) {
        await this.loadScript(this.scripts.pdf, 'pdfjsLib');
        // Setup worker
        if (!window.pdfjsLib.GlobalWorkerOptions.workerSrc) {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = this.scripts.pdfWorker;
        }

        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = window.pdfjsLib.getDocument(arrayBuffer);
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        const scale = 0.5; // Thumbnail size
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({ canvasContext: context, viewport }).promise;
        return canvas.toDataURL();
    }

    async renderPSD(file) {
        await this.loadScript(this.scripts.psd, 'PSD');
        const url = URL.createObjectURL(file);

        // PSD.js helper
        return new Promise((resolve, reject) => {
            window.PSD.fromURL(url).then(psd => {
                const image = psd.image.toPng();

                // psd.image.toPng() returns an HTMLImageElement
                if (image && image.tagName === 'IMG') {
                    // Wait for load? Usually it's already loaded from data URI inside psd.js
                    const canvas = document.createElement('canvas');
                    canvas.width = image.width;
                    canvas.height = image.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(image, 0, 0);
                    resolve(canvas.toDataURL('image/png', 0.5));
                } else {
                    // Fallback if it returns something else
                    try {
                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');
                        canvas.width = psd.image.width();
                        canvas.height = psd.image.height();
                        const pixelData = psd.image.pixelData();
                        const data = context.createImageData(canvas.width, canvas.height);
                        for (let i = 0; i < pixelData.length; i++) {
                            data.data[i] = pixelData[i];
                        }
                        context.putImageData(data, 0, 0);
                        resolve(canvas.toDataURL('image/png', 0.5));
                    } catch (e) {
                        reject(e);
                    }
                }
            }).catch(e => {
                console.error("PSD Parse Error:", e);
                reject(e);
            });
        });
    }
    async renderTIFF(file) {
        await this.loadScript(this.scripts.tiff, 'UTIF');
        const arrayBuffer = await file.arrayBuffer();
        const ifds = window.UTIF.decode(arrayBuffer);

        if (!ifds || ifds.length === 0) throw new Error("No IFDs found");

        window.UTIF.decodeImage(arrayBuffer, ifds[0]);
        const rgba = window.UTIF.toRGBA8(ifds[0]);

        const canvas = document.createElement('canvas');
        canvas.width = ifds[0].width;
        canvas.height = ifds[0].height;
        const ctx = canvas.getContext('2d');
        const imgData = ctx.createImageData(canvas.width, canvas.height);

        for (let i = 0; i < rgba.length; i++) {
            imgData.data[i] = rgba[i];
        }
        ctx.putImageData(imgData, 0, 0);
        return canvas.toDataURL('image/png', 0.5);
    }
}

window.EXCLUSION_RULES = EXCLUSION_RULES;
window.STANDARD_SIZE_DICT = STANDARD_SIZE_DICT;
window.ImageMetadataReader = ImageMetadataReader;
window.RFModal = RFModal;
window.ThumbnailGenerator = ThumbnailGenerator;
window.preventDefaults = preventDefaults;
